import { useId, useRef, useState, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import clsx from "clsx";
import { parseTrackingNumber } from "../../../../api/statusService";
import DividerWithText from "../../../../components/dividerWithText";
import ParseFailedModal from "../../../../components/modals/ParseFailedModal";
import TrackingConfirmModal from "../../../../components/modals/TrackingConfirmModal";
import PhotoCaptureBox from "../../../../components/photoCaptureBox";
import { carrierOptions } from "../../../../constants";
import { ANALYTICS_EVENTS, trackEvent } from "../../../../lib/analytics";
import type { TrackingSubmission } from "../../../../hooks/useUpdateShippingStatus";

type TrackingFormValues = { carrier: string };

interface TrackingEntryFormProps {
  orderId: string;
  submitLabel: ReactNode;
  isSubmitting: boolean;
  onSubmit: (submission: TrackingSubmission) => void;
}

export default function TrackingEntryForm({
  orderId,
  submitLabel,
  isSubmitting,
  onSubmit,
}: TrackingEntryFormProps) {
  const inputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { control, handleSubmit, setValue } = useForm<TrackingFormValues>({
    defaultValues: { carrier: "" },
  });
  const selectedCarrier = useWatch({ control, name: "carrier" });

  const [trackingId, setTrackingId] = useState("");
  const [prevTrackingId, setPrevTrackingId] = useState<string | null>(null);
  const [trackingImageFile, setTrackingImageFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [trackingManualEntryTracked, setTrackingManualEntryTracked] =
    useState(false);

  const parseMutation = useMutation({
    mutationFn: (file: File) => parseTrackingNumber(file, orderId),
    onSuccess: (res) => {
      if (res?.carrier && res.carrier.toUpperCase() !== "UNKNOWN") {
        setValue("carrier", res.carrier);
      } else {
        setValue("carrier", "");
      }
      const extractedId = res?.trackingNumber;
      if (
        extractedId &&
        extractedId.toUpperCase() !== "UNKNOWN" &&
        extractedId.length < 30
      ) {
        setParsedData(extractedId);
        setShowConfirmModal(true);
        trackEvent(ANALYTICS_EVENTS.TRACKING_PARSE_SUCCESS);
      } else {
        setShowFailedModal(true);
        trackEvent(ANALYTICS_EVENTS.TRACKING_PARSE_FAILED, {
          reason: "invalid_result",
        });
      }
    },
    onError: () => {
      setShowFailedModal(true);
      trackEvent(ANALYTICS_EVENTS.TRACKING_PARSE_FAILED, {
        reason: "api_error",
      });
    },
  });

  const resetTrackingPhoto = () => {
    setTrackingImageFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (prevTrackingId !== null) {
      setTrackingId(prevTrackingId);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPrevTrackingId(trackingId);
      setTrackingImageFile(file);
      trackEvent(ANALYTICS_EVENTS.TRACKING_PHOTO_TAKEN);
      parseMutation.mutate(file);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleConfirm = () => {
    if (parsedData) setTrackingId(parsedData);
    setShowConfirmModal(false);
    setParsedData(null);
  };

  const reopenTrackingPicker = () => {
    const input = fileInputRef.current as
      | (HTMLInputElement & { showPicker?: () => void })
      | null;
    if (!input) return;

    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }

    input.click();
  };

  const submit = ({ carrier }: TrackingFormValues) => {
    trackEvent(ANALYTICS_EVENTS.TRACKING_SUBMITTED);
    onSubmit({
      trackingInfo: { trackingNumber: trackingId, carrier },
      trackingImage: trackingImageFile,
    });
  };

  return (
    <>
      <input
        type="file"
        id={inputId}
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        className="absolute h-0 w-0 opacity-0 -z-10"
      />

      <div
        className={clsx(
          "space-y-4 animate-in fade-in slide-in-from-top-2",
          parseMutation.isPending &&
            "pointer-events-none select-none opacity-50",
        )}
      >
        <PhotoCaptureBox
          inputId={inputId}
          isParsing={parseMutation.isPending}
          hasCapturedPhoto={!!trackingImageFile}
          idleText="Take a picture of the tracking label or receipt"
          parsingText="Scanning image..."
          onReset={resetTrackingPhoto}
        />

        <DividerWithText text="No label or receipt? Enter the tracking number manually" />

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-slate-600">
            Tracking ID
          </label>
          <input
            type="text"
            value={trackingId}
            onChange={(e) => {
              setTrackingId(e.target.value);
              if (!trackingManualEntryTracked && e.target.value) {
                trackEvent(ANALYTICS_EVENTS.TRACKING_MANUAL_ENTRY);
                setTrackingManualEntryTracked(true);
              }
            }}
            placeholder="Enter your tracking number"
            className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <Controller
          name="carrier"
          control={control}
          rules={{ required: "Carrier is required" }}
          render={({ field }) => (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-slate-600">
                Carrier
              </label>

              <Listbox value={field.value} onChange={field.onChange}>
                <div className="relative">
                  <ListboxButton className="flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3.5 text-sm outline-none focus:ring-2 focus:ring-blue-500">
                    <span
                      className={clsx(
                        "truncate",
                        !field.value && "text-gray-400",
                      )}
                    >
                      {field.value || "Select carrier"}
                    </span>
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  </ListboxButton>
                  <ListboxOptions
                    anchor="bottom"
                    className="mt-1 w-(--button-width) rounded-xl border border-gray-200 bg-white p-1 shadow-lg outline-none z-20"
                  >
                    {carrierOptions.map((carrier) => (
                      <ListboxOption
                        key={carrier}
                        value={carrier}
                        className="cursor-pointer select-none rounded-lg px-3 py-2 text-sm data-focus:bg-blue-50 data-focus:text-blue-500"
                      >
                        {carrier}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </div>
              </Listbox>
            </div>
          )}
        />

        <button
          type="button"
          onClick={handleSubmit(submit)}
          disabled={!trackingId || !selectedCarrier || isSubmitting}
          className="w-full py-4 rounded-xl font-semibold transition-colors shadow-md shadow-blue-200 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 cursor-pointer flex items-center justify-center gap-2"
        >
          {submitLabel}
        </button>
      </div>

      <TrackingConfirmModal
        isOpen={showConfirmModal}
        parsedData={parsedData}
        onClose={() => {
          setShowConfirmModal(false);
          setTrackingImageFile(null);
        }}
        onConfirm={handleConfirm}
      />
      <ParseFailedModal
        isOpen={showFailedModal}
        onClose={() => {
          setShowFailedModal(false);
          setTrackingImageFile(null);
        }}
        onTryAgain={() => {
          setShowFailedModal(false);
          reopenTrackingPicker();
        }}
      />
    </>
  );
}
